import { use, useCallback, useEffect, useRef } from "react";

import { mergeClassNames } from "@bokio/utils/classes";

import Field from "./Field/Field.internals";
import { FormContext } from "./Form";
import LabelFor from "./LabelFor/LabelFor";

import type { FormContextValue } from "./Form";
import type { LabelTooltip } from "./LabelFor/LabelFor";
import type { FieldValidationResult } from "@bokio/shared/validation/entityValidator";
import type { ChangeEvent, ClipboardEvent, ReactNode } from "react";

import * as styles from "./form.scss";

interface TextAreaFieldProps {
	value?: string;
	onChange?: (value: string) => void;
	name?: string;
	label?: ReactNode;
	hint?: ReactNode;
	info?: ReactNode;
	disabled?: boolean;
	/**
	 *  Minimum height of the input
	 */
	minHeight?: string;
	/**
	 *  Minimum number of rows in the input, defaults to 1
	 */
	minRows?: number;
	/**
	 * Validation errors from validators using `FieldRuleFactory`.
	 */
	errors?: FieldValidationResult;
	placeholder?: string;
	/**
	 * Set class name to the actual textarea element.
	 * @deprecated add props to handle the change instead of using className
	 */
	className?: string;
	autoFocus?: boolean;
	maxLength?: number;
	testId?: string;
	labelTestId?: string;
	onHeightChange?: (height: number) => void;
	/**
	 * Append a tooltip icon next to the textarea field.
	 */
	tooltip?: LabelTooltip;
	/**
	 * Marks the field as required by:
	 * - Adding an asterisk (*) next to the label
	 * - Setting the `aria-required` attribute on the textarea element for accessibility
	 *
	 * Note: This does not add validation. To validate that the field is required,
	 * use `FieldValidator` with `FieldRuleFactory.Required()`.
	 */
	markAsRequired?: boolean;
	onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
}

export function TextAreaField({
	label,
	hint,
	info,
	minHeight,
	name,
	value,
	minRows: rows,
	placeholder,
	errors,
	className,
	disabled,
	autoFocus,
	testId,
	maxLength,
	tooltip,
	labelTestId,
	onHeightChange,
	onChange,
	onPaste,
	markAsRequired = false,
}: TextAreaFieldProps) {
	const { disableAllFields } = use<FormContextValue>(FormContext);
	const fieldRef = useRef<HTMLTextAreaElement>(null);

	const autoGrow = useCallback(() => {
		if (fieldRef.current) {
			const borderHeight = fieldRef.current.offsetHeight - fieldRef.current.clientHeight;
			fieldRef.current.style.minHeight = minHeight || "auto";
			fieldRef.current.style.height = "auto";
			const newHeight = fieldRef.current.scrollHeight + borderHeight;
			fieldRef.current.style.height = newHeight + "px";
			if (onHeightChange !== undefined) {
				onHeightChange(newHeight);
			}
		}
	}, [onHeightChange, minHeight]);

	useEffect(() => {
		if (value === undefined || value === "") {
			autoGrow();
		}
	}, [value, autoGrow]);

	const handleInputChanged = (event: ChangeEvent<HTMLTextAreaElement>) => {
		if (onChange) {
			onChange(event.currentTarget.value);
		}
		autoGrow();
	};

	const style = { height: minHeight };

	return (
		<Field>
			<LabelFor
				label={label}
				info={info}
				errors={errors && errors.errors}
				tooltip={tooltip}
				testId={labelTestId}
				markAsRequired={markAsRequired}
			>
				<textarea
					data-testid={testId}
					ref={fieldRef}
					className={mergeClassNames(styles.field__input, styles.textArea, className)}
					style={style}
					name={name}
					value={value ?? ""}
					maxLength={maxLength}
					rows={rows || 1}
					onChange={handleInputChanged}
					onPaste={onPaste}
					disabled={disableAllFields || disabled}
					placeholder={placeholder}
					autoFocus={autoFocus}
					aria-required={markAsRequired}
				/>
			</LabelFor>
			{hint && <small className={styles.hint}>{hint}</small>}
		</Field>
	);
}
